import React, { useEffect, useState, useCallback, useMemo } from "react";
import Order from "./Order";
import DetailsModal from "./DetailsModal";
import "../Css/Incomplete.css";
import Logger from "./Logger";
import PaginationControls from "../Component/Pagination";
import { Typography, CircularProgress, Dialog, DialogTitle, DialogContent, IconButton, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { FaListUl } from "react-icons/fa";
import { connectSocket } from "../utils/connectSocket";
import { getLoggerBaseURL } from "../utils/getLoggerBaseURL";

function Incomplete() {
    const userEmail = sessionStorage.getItem("userEmail");
    const [selectedOption, setSelectedOption] = useState("OrderNo");
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");
    const [orders, setOrders] = useState([]);
    const [showDModal, setShowDModal] = useState(false);
    const [selectedOrderNo, setSelectedOrderNo] = useState(null);
    const [images, setImages] = useState([]);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 50;
    const [loading, setLoading] = useState(true);
    const [openImageModal, setOpenImageModal] = useState(false);
    const [totalOrders, setTotalOrders] = useState(0);
    const navigate = useNavigate();

    const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

    useEffect(() => {
        const socket = connectSocket();

        socket.on("success", (order) => {
            setOrders((prevOrders) => {
                const orderIndex = prevOrders.findIndex((o) => o._id === order._id);
                if (orderIndex !== -1) {
                    return prevOrders.map((o) =>
                        o._id === order._id
                            ? { ...order, driverName: order.driverName || o.driverName }
                            : o
                    );
                } else {
                    return [...prevOrders, order];
                }
            });
        });

        return () => {
            socket.disconnect();
        };
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, startDate, endDate, selectedOption]);

    const handleOrderDetailsClick = async (orderId) => {
        setShowDModal(true);
        const selectedOrder = orders.find((order) => order._id === orderId);
        setSelectedOrder(selectedOrder);
    };

    const handleOpenModal = async (orderNo) => {
        const fileName = `${orderNo}.jpg`;
        const encodedFileName = encodeURIComponent(fileName);

        setSelectedOrderNo(orderNo); // keep clean orderNo for display
        setOpenImageModal(true);

        try {
            const response = await fetch(`/api/ordera/image/${encodedFileName}`);
            const data = await response.json();

            if (data.files && data.files.length > 0) {
                setImages(data.files.map(f => f.url));   // only keep the URLs
                setCurrentImageIndex(0);
            } else {
                setImages([]);
            }
        } catch (error) {
            logger.log(`Error fetching images for order ${orderNo}: ${error.message}`, "ERROR");
            setImages([]);
        }
    };

    const handleCloseImageModal = () => {
        setOpenImageModal(false);
        setImages([]);
        setCurrentImageIndex(0);
    };

    const fetchOrders = useCallback(async (page = 1) => {
        try {
            setLoading(true);

            const params = new URLSearchParams({
                page,
                limit: 50,
                selectedOption,
                keyword: searchQuery || "",
            });
            if (startDate && endDate) {
                params.set("startDate", startDate);
                params.set("endDate", endDate);
            }

            const res = await fetch(`/api/ordera/orderIncomplete/${userEmail}?${params.toString()}`);

            const json = await res.json();
            setTotalOrders(json.totalOrders || 0);

            setOrders(json.data);
            setLoading(false);
        } catch (error) {
            logger.log(`Error fetching orders for email ${userEmail}: ${error.message}`, "ERROR");
            setOrders([]); // Set orders to an empty array in case of error
        } finally {
            setLoading(false); // Ensure loading state is updated even on error
        }
    }, [userEmail, startDate, endDate, selectedOption, searchQuery, logger]);

    useEffect(() => {
        fetchOrders(1);
    }, [fetchOrders, selectedOption, startDate, endDate, searchQuery]);

    const handleCloseModal = () => {
        setShowDModal(false);
        setSelectedOrder(null);
    };

    const handleViewHistory = (orderId) => {
        navigate(`/timeline/${orderId}`);
    };

    return (
        <div>
            <Order
                searchQuery={searchQuery}
                setSearchQuery={(val) => {
                    setSearchQuery(val);
                    setCurrentPage(1);
                }}
                selectedOption={selectedOption}
                setSelectedOption={(val) => {
                    setSelectedOption(val);
                    setCurrentPage(1);
                }}
                startDate={startDate}
                setStartDate={(val) => {
                    setStartDate(val);
                    setCurrentPage(1);
                }}
                endDate={endDate}
                setEndDate={(val) => {
                    setEndDate(val);
                    setCurrentPage(1);
                }}
            />
            <div>
                {loading ? (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100vh" }}>
                        <CircularProgress />
                        <Typography variant="h6" style={{ marginTop: "20px" }}>
                            Fetching data...
                        </Typography>
                    </div>
                ) : (
                    <>
                        <table className="incompleteOrdersTable">
                            <thead>
                                <tr>
                                    <th className="Ith">
                                        <strong>Delivery Date</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Order No.</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>C.Name</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>C.Address</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Amount</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Distance</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Start Time</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Assign Time</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Pickup Time</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Req. Delivery Time</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Order Fail Time</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Driver</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Reason of Failure</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Comments</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>Proof of Failure</strong>
                                    </th>
                                    <th className="Ith">
                                        <strong>History</strong>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {orders.length === 0 ? (
                                    <tr>
                                        <td className="noOrders" colSpan="21">
                                            <div className="center-content">
                                                <div className="icon-wrapper">
                                                    <FaListUl className="icon" />
                                                </div>
                                                <span className="noOrdersMsg">No Incomplete Orders found</span>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    orders.map((order) => (
                                        <tr key={order._id}>
                                            <td className="tdCu">
                                                {order.Del_Date || "N/A"}
                                            </td>
                                            <td className="tdCu">
                                                <button className="detailsD" onClick={() => handleOrderDetailsClick(order._id)}                                                >
                                                    <b>{order.Order_No || "N/A"}</b>
                                                </button>
                                            </td>
                                            <td className="tdCu">{order.Rec_Name || "N/A"}</td>
                                            <td className="tdCu">{order.Rec_Add || "N/A"}</td>
                                            <td className="tdCu">₹ {order.amount || "N/A"}</td>
                                            <td className="tdCu">{order.distance || "N/A"}</td>
                                            <td className="tdCu">{order.o_time || "N/A"}</td>
                                            <td className="tdCu">{order.assign_time || "N/A"}</td>
                                            <td className="tdCu">{order.Pick_Time_Actual || "N/A"}</td>
                                            <td className="tdCu">{order.Del_Time || "N/A"}</td>
                                            <td className="tdCu">{order.Del_Time_Actual || "N/A"}</td>
                                            <td className="tdCu">{order.driverName || "N/A"}</td>
                                            <td className="tdCu">{order.reason_failure || "N/A"}</td>
                                            <td className="tdCu">{order.comments || "N/A"}</td>
                                            <td className="tdC">
                                                <button className="detailsDD" onClick={() => handleOpenModal(order.Order_No)}>View Image</button>
                                            </td>
                                            <td className="tdC">
                                                <button className="detailsDD" onClick={() => handleViewHistory(order._id)}>View History</button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem", marginRight: "30px", }}>
                            <PaginationControls totalItems={totalOrders} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
                        </div>
                    </>
                )}
            </div>

            {/* Details Modal */}
            {showDModal && selectedOrder && (
                <DetailsModal showDModal={showDModal} onClose={handleCloseModal} selectedOrder={selectedOrder} BackdropProps={{ style: { backgroundColor: "rgba(0,0,0,0.1)" } }} />
            )}

            {/* Image Modal */}
            <Dialog open={openImageModal} onClose={handleCloseImageModal} maxWidth="md" fullWidth>
                <DialogTitle sx={{ textAlign: "center", position: "relative", fontWeight: "bold", }}>
                    Proof of Failure
                    <IconButton aria-label="close" onClick={handleCloseImageModal} sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500], }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "50vh", }}>
                    {images.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                            <img
                                src={images[currentImageIndex]}
                                alt={`Order ${selectedOrderNo}`}
                                loading="lazy"
                                style={{
                                    maxWidth: "100%",
                                    maxHeight: "70vh",
                                    objectFit: "contain",
                                    display: "block"
                                }}
                            />
                            <div style={{ marginTop: 8, display: "flex", gap: 8, alignItems: "center" }}>
                                <Button
                                    onClick={() => setCurrentImageIndex(i => Math.max(0, i - 1))}
                                    disabled={currentImageIndex === 0}
                                >
                                    Prev
                                </Button>
                                <Typography>
                                    {currentImageIndex + 1} / {images.length}
                                </Typography>
                                <Button
                                    onClick={() => setCurrentImageIndex(i => Math.min(images.length - 1, i + 1))}
                                    disabled={currentImageIndex === images.length - 1}
                                >
                                    Next
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <Typography sx={{ textAlign: "center" }}>No image found for this order</Typography>
                    )}
                </DialogContent>
            </Dialog>
        </div >
    );
}

export default Incomplete;
